import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type {
    CommitDividendDeclarationResponse,
    CreateShareholderResponse,
    DividendDistributionRequest,
    PreviewDividendDeclarationResponse,
    ShareholderWriteRequest,
    UpdateShareholderResponse,
} from '@/api/handlers/resources/dividends';
import type { ApiResponse } from '@/api/handlers/responses';
import type { JournalEntry } from '@/api/models';
import { AccountQueryKeys } from '../accounts/accountQueryKeys';
import { JournalQueryKeys } from '../journal/journalQueryKeys';
import { StatementQueryKeys } from '../statements/statementQueryKeys';
import { DividendQueryKeys } from './dividendQueryKeys';

/** Create a shareholder. Touches no ledger account, so only the register refetches. */
export const useCreateShareholderMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<CreateShareholderResponse>,
        Error,
        ShareholderWriteRequest
    >({
        mutationFn: (body) => apiClient.dividends.createShareholder(body),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: DividendQueryKeys.all,
                });
            }
        },
    });
};

/**
 * Edit a shareholder.
 *
 * Invalidates the whole dividends key rather than just the register, because a
 * changed holding or withholding rate changes every cached calculator quote too.
 */
export const useUpdateShareholderMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<UpdateShareholderResponse>,
        Error,
        { shareholderId: string; body: ShareholderWriteRequest }
    >({
        mutationFn: ({ shareholderId, body }) =>
            apiClient.dividends.updateShareholder(shareholderId, body),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: DividendQueryKeys.all,
                });
            }
        },
    });
};

/**
 * Remove a shareholder no declaration has ever paid.
 *
 * Refused with `SHAREHOLDER_HAS_DECLARATIONS` for anyone who has been, and the
 * caller shows that rather than retrying — there is no force flag. Only the
 * register refetches: a deletable holder is by definition one no entry was ever
 * built from, so no ledger balance can have moved.
 */
export const useDeleteShareholderMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<void>, Error, string>({
        mutationFn: (shareholderId) =>
            apiClient.dividends.deleteShareholder(shareholderId),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: DividendQueryKeys.all,
                });
            }
        },
    });
};

/**
 * Preview a declaration: the per-shareholder split and the draft a commit would
 * build. **Pure** — it writes nothing, so it invalidates nothing.
 */
export const usePreviewDeclarationMutation = () => {
    const { apiClient } = useApi();

    return useMutation<
        ApiResponse<PreviewDividendDeclarationResponse>,
        Error,
        DividendDistributionRequest
    >({
        mutationFn: (body) => apiClient.dividends.previewDeclaration(body),
    });
};

/**
 * Create the declaration's DRAFT entry and record it.
 *
 * **Nothing is posted**, so nothing in the ledger has moved: the accounts and the
 * statements are deliberately *not* invalidated here. What has changed is the
 * register — the history gains a row — and the journal, which gains a draft.
 *
 * The account balances move later, when someone posts that draft. That is what
 * {@link useInvalidatePostedDividendEntry} is for.
 */
export const useCommitDeclarationMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<CommitDividendDeclarationResponse>,
        Error,
        DividendDistributionRequest
    >({
        mutationFn: (body) => apiClient.dividends.commitDeclaration(body),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: DividendQueryKeys.all,
                });
                queryClient.invalidateQueries({
                    queryKey: JournalQueryKeys.all,
                });
            }
        },
    });
};

/**
 * The declaration's entry was **posted** — tell the four feeds that just moved.
 *
 * Posting is the only event that changes what the register reports: every accrued
 * figure comes from declarations whose entry has actually posted, and the ledger
 * side of the tie-out is the account balances. The journal's own post mutation
 * invalidates only the journal, so without this the summary and the comparison
 * keep showing their pre-post state — a committed declaration reading as money
 * owed when nothing has been charged, or the reverse.
 *
 * The statements move too: a distribution debits retained earnings.
 *
 * A save that did not post moves nothing: the draft already existed.
 */
export const useInvalidatePostedDividendEntry = () => {
    const queryClient = useQueryClient();

    return React.useCallback(
        (entry: Pick<JournalEntry, 'status'> | null | undefined) => {
            if (entry?.status !== 'POSTED') return;
            queryClient.invalidateQueries({ queryKey: DividendQueryKeys.all });
            queryClient.invalidateQueries({ queryKey: JournalQueryKeys.all });
            // The balances the accrued-vs-ledger tie-out reads.
            queryClient.invalidateQueries({ queryKey: AccountQueryKeys.all });
            // Retained earnings and the unclosed result, which gate the next
            // declaration.
            queryClient.invalidateQueries({ queryKey: StatementQueryKeys.all });
        },
        [queryClient],
    );
};
